import React, { useState, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import useLoadMore from '../utils/useLoadMore';
import LoadMoreButton from './LoadMoreButton';
import { getUserDisplayName } from '../utils/userUtils';

const AllReflections = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedFeeling, setSelectedFeeling] = useState('all');
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filterOptions = [
    { id: 'all', name: '全部体式反思', emoji: '📝' },
    { id: 'recent', name: '最近一周', emoji: '🕐' },
    { id: 'month', name: '本月', emoji: '📅' },
    { id: 'high_energy', name: '高能量', emoji: '⚡' },
    { id: 'deep_focus', name: '深度专注', emoji: '🎯' },
    { id: 'breakthrough', name: '突破时刻', emoji: '🌟' }
  ];

  // 加载体式反思数据
  useEffect(() => {
    const loadReflections = async () => {
      try {
        setLoading(true);
        setError(null);
        const reflectionsData = await dataManager.getPracticeReflections();

        // 转换数据格式
        const formattedReflections = reflectionsData.map(reflection => ({
          ...reflection,
          focusedPoses: reflection.focusedPoses || (Array.isArray(reflection.focused_poses)
            ? reflection.focused_poses.map(pose => pose.chinese_name || pose.chineseName || pose.name || '')
            : (reflection.poseRecords ? reflection.poseRecords.map(record => record.selectedPose) : [])),
          content: reflection.content || reflection.overallReflection || '',
          energy: reflection.energy ?? reflection.energy_level ?? 0,
          focus: reflection.focus ?? reflection.focus_level ?? 0,
          flexibility: reflection.flexibility ?? reflection.flexibility_level ?? 0,
          tags: reflection.tags || []
        }));

        setReflections(formattedReflections);
      } catch (err) {
        console.error('Error loading reflections:', err);
        setError('加载体式反思失败，请确保Django服务器正在运行');
        setReflections([]); // 服务器模式失败时设置为空数组
      } finally {
        setLoading(false);
      }
    };

    loadReflections();
  }, []);

  // 从数据管理器中获取数据
  const feelingOptions = [
    { id: 'all', name: '全部感受', emoji: '🌈' },
    ...dataManager.getFeelingOptions()
  ];

  // 数据现在通过useEffect异步加载到reflections状态中




  const filteredReflections = reflections.filter(reflection => {
    if (selectedFeeling !== 'all' && reflection.feeling !== selectedFeeling) {
      return false;
    }
    
    if (selectedFilter === 'recent') {
      const reflectionDate = new Date(reflection.date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return reflectionDate >= weekAgo;
    }
    
    if (selectedFilter === 'month') {
      const reflectionDate = new Date(reflection.date);
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return reflectionDate >= monthAgo;
    }
    
    if (selectedFilter === 'high_energy') {
      return reflection.energy >= 8;
    }
    
    if (selectedFilter === 'deep_focus') {
      return reflection.focus >= 9;
    }
    
    if (selectedFilter === 'breakthrough') {
      return reflection.tags.some(tag => ['突破', '成就感', '进步'].includes(tag));
    }
    
    return true;
  });

  // 渐进式分页：首批 10 条，"加载更多"追加，筛选变化时自动重置
  const {
    visibleItems: visibleReflections,
    hasMore: hasMoreReflections,
    remaining: remainingReflections,
    loadMore: loadMoreReflections,
  } = useLoadMore(filteredReflections, 10, `${selectedFilter}|${selectedFeeling}`);

  const getEnergyColor = (energy) => {
    if (energy >= 8) return 'text-red-500';
    if (energy >= 6) return 'text-orange-500';
    return 'text-green-500';
  };

  const getFocusColor = (focus) => {
    if (focus >= 8) return 'text-blue-500';
    if (focus >= 6) return 'text-indigo-500';
    return 'text-purple-500';
  };

  // 加载状态
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white text-2xl">🧘‍♀️</span>
          </div>
          <p className="text-emerald-600">正在加载体式反思...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">⚠️</span>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-emerald-800 mb-4">练习历史</h1>
        <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
          浏览社区中所有练习者的体式反思记录，从他人的经验中获得启发
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/70 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-emerald-600">{reflections.length}</div>
          <div className="text-sm text-emerald-700 mt-1">总体式反思数</div>
        </div>
        <div className="bg-white/70 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-blue-600">
            {reflections.length > 0 ? Math.round(reflections.reduce((sum, r) => sum + (r.energy || 0), 0) / reflections.length) : 0}
          </div>
          <div className="text-sm text-blue-700 mt-1">平均能量</div>
        </div>
        <div className="bg-white/70 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-purple-600">
            {reflections.length > 0 ? Math.round(reflections.reduce((sum, r) => sum + (r.focus || 0), 0) / reflections.length) : 0}
          </div>
          <div className="text-sm text-purple-700 mt-1">平均专注</div>
        </div>
        <div className="bg-white/70 rounded-2xl p-6 text-center">
          <div className="text-3xl font-bold text-teal-600">
            {new Set(reflections.map(r => r.user)).size}
          </div>
          <div className="text-sm text-teal-700 mt-1">活跃用户</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/70 rounded-2xl p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-emerald-800 mb-3">筛选条件</h3>
          <div className="flex flex-wrap gap-3">
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedFilter(option.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedFilter === option.id
                    ? 'bg-emerald-500 text-white shadow-lg'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                }`}
              >
                <span className="mr-2">{option.emoji}</span>
                {option.name}
              </button>
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold text-emerald-800 mb-3">感受类型</h3>
          <div className="flex flex-wrap gap-3">
            {feelingOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedFeeling(option.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedFeeling === option.id
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <span className="mr-2">{option.emoji}</span>
                {option.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Reflections List */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-emerald-800">
          体式反思记录
          <span className="text-lg font-normal text-emerald-600 ml-2">
            ({filteredReflections.length} 条记录)
          </span>
        </h3>
        
        <div className="grid gap-6">
          {visibleReflections.map((reflection) => (
            <div key={reflection.id} className="bg-white/70 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <h4 className="text-lg font-semibold text-emerald-800">
                      {getUserDisplayName(reflection.user)}
                    </h4>
                    <span className="text-sm text-gray-500">{reflection.date} {reflection.time}</span>
                    <span className="flex items-center text-sm">
                      {feelingOptions.find(f => f.id === reflection.feeling)?.emoji}
                      <span className="ml-1 text-emerald-600">
                        {feelingOptions.find(f => f.id === reflection.feeling)?.name}
                      </span>
                    </span>
                  </div>
                  <div className="text-sm text-emerald-600 mb-3">
                    指导老师: {reflection.teacher}
                  </div>
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {reflection.content}
              </p>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">能量:</span>
                    <span className={`font-semibold ${getEnergyColor(reflection.energy)}`}>
                      {reflection.energy}/10
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">专注:</span>
                    <span className={`font-semibold ${getFocusColor(reflection.focus)}`}>
                      {reflection.focus}/10
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">柔韧:</span>
                    <span className="font-semibold text-teal-500">
                      {reflection.flexibility}/10
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-emerald-600 font-medium">重点体式:</span>
                    {Array.isArray(reflection.focusedPoses) ? reflection.focusedPoses.map((pose, index) => (
                      <span key={index} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs">
                        {pose}
                      </span>
                    )) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-gray-600 font-medium">标签:</span>
                    {Array.isArray(reflection.tags) ? reflection.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                        #{tag}
                      </span>
                    )) : null}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button className="text-emerald-600 hover:text-emerald-700 transition-colors">
                    点赞
                  </button>
                  <button className="text-emerald-600 hover:text-emerald-700 transition-colors">
                    分享
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <LoadMoreButton hasMore={hasMoreReflections} remaining={remainingReflections} onClick={loadMoreReflections} />
      </div>
    </div>
  );
};

export default AllReflections;
